import * as React from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    BooleanInput,
    SelectInput,
    Labeled,
    TextField,
    TopToolbar,
    SaveButton,
    DeleteButton,
    Toolbar,
    useTranslate,
    useRecordContext,
} from 'react-admin';
import { Link } from 'react-router-dom';
import {
    Grid,
    Button,
    Typography,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Box,
    Link as MuiLink,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import InfoSchemaForm from '../../components/InfoSchemaForm';

const EditActions = () => {
    const translate = useTranslate();
    return (
        <TopToolbar>
            <Button
                startIcon={<ArrowBackIcon />}
                component={Link}
                to="/hallinta?tab=6"
                size="small"
            >
                {translate('hallinta.api_configs.actions.back')}
            </Button>
        </TopToolbar>
    );
};

const EditToolbar = () => (
    <Toolbar sx={{ justifyContent: 'space-between' }}>
        <SaveButton />
        <DeleteButton mutationMode="pessimistic" />
    </Toolbar>
);

const JwtKeysTable = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const keys = record?.jwt_keys || [];

    if (keys.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                {translate('hallinta.api_configs.no_jwt_keys')}
            </Typography>
        );
    }

    return (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>KID</TableCell>
                        <TableCell>Env</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Algorithm</TableCell>
                        <TableCell>Created</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {keys.map((key: any) => (
                        <TableRow key={key.kid}>
                            <TableCell
                                sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                }}
                            >
                                <MuiLink
                                    component={Link}
                                    to={`/hallinta?tab=4`}
                                    underline="hover"
                                >
                                    {key.kid?.substring(0, 8)}...
                                </MuiLink>
                            </TableCell>
                            <TableCell>
                                <Chip label={key.env || 'all'} size="small" />
                            </TableCell>
                            <TableCell>
                                <Chip
                                    label={key.status}
                                    size="small"
                                    color={
                                        key.status === 'active'
                                            ? 'success'
                                            : 'default'
                                    }
                                    variant="outlined"
                                />
                            </TableCell>
                            <TableCell>{key.algorithm}</TableCell>
                            <TableCell>
                                {key.created
                                    ? new Date(key.created).toLocaleDateString(
                                          'fi-FI'
                                      )
                                    : '—'}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const OAuthClientsTable = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const clients = record?.oauth_clients || [];

    if (clients.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                {translate('hallinta.api_configs.no_oauth_clients')}
            </Typography>
        );
    }

    return (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Client ID</TableCell>
                        <TableCell>Env</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {clients.map((client: any) => (
                        <TableRow key={client.guid}>
                            <TableCell>
                                <MuiLink
                                    component={Link}
                                    to={`/admin/oauth-clients/${client.guid}`}
                                    underline="hover"
                                >
                                    {client.name}
                                </MuiLink>
                            </TableCell>
                            <TableCell
                                sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                }}
                            >
                                {client.client_id || '—'}
                            </TableCell>
                            <TableCell>
                                {Array.isArray(client.env)
                                    ? client.env.map((e: string) => (
                                          <Chip
                                              key={e}
                                              label={e}
                                              size="small"
                                              sx={{ mr: 0.5 }}
                                          />
                                      ))
                                    : client.env || '—'}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const ApiConfigEditForm = () => {
    const translate = useTranslate();

    return (
        <>
            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 5 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 2 }}
                    >
                        <InfoOutlinedIcon
                            fontSize="small"
                            sx={{ color: 'black' }}
                        />
                        <Typography variant="subtitle1">
                            {translate('hallinta.api_configs.sections.basic')}
                        </Typography>
                    </Stack>
                    <Labeled
                        label={translate(
                            'hallinta.api_configs.fields.application_name'
                        )}
                    >
                        <TextField source="application_name" />
                    </Labeled>
                    <TextInput
                        source="key"
                        label={translate('hallinta.api_configs.fields.key')}
                        fullWidth
                    />
                    <TextInput
                        source="name"
                        label={translate('hallinta.api_configs.fields.name')}
                        fullWidth
                    />
                    <BooleanInput
                        source="is_default"
                        label={translate(
                            'hallinta.api_configs.fields.is_default'
                        )}
                    />
                    <Labeled
                        label={translate('hallinta.api_configs.fields.type')}
                    >
                        <TextField source="type" />
                    </Labeled>
                    <SelectInput
                        source="status"
                        label="Status"
                        choices={[
                            { id: 0, name: 'Active' },
                            { id: 1, name: 'Deleted' },
                        ]}
                        fullWidth
                    />

                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mt: 3, mb: 1 }}
                    >
                        <SettingsOutlinedIcon
                            fontSize="small"
                            sx={{ color: 'black' }}
                        />
                        <Typography variant="subtitle1">
                            {translate(
                                'hallinta.api_configs.sections.json_config'
                            )}
                        </Typography>
                    </Stack>
                    <Box>
                        <InfoSchemaForm
                            resource="api-config"
                            type="openid"
                            source="info"
                        />
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 7 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 2 }}
                    >
                        <VpnKeyOutlinedIcon
                            fontSize="small"
                            sx={{ color: 'black' }}
                        />
                        <Typography variant="subtitle1">
                            {translate(
                                'hallinta.api_configs.sections.jwt_keys'
                            )}
                        </Typography>
                    </Stack>
                    <JwtKeysTable />

                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mt: 3, mb: 1 }}
                    >
                        <PeopleOutlinedIcon
                            fontSize="small"
                            sx={{ color: 'black' }}
                        />
                        <Typography variant="subtitle1">
                            {translate(
                                'hallinta.api_configs.sections.oauth_clients'
                            )}
                        </Typography>
                    </Stack>
                    <Box>
                        <OAuthClientsTable />
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};

const ApiConfigEdit = () => {
    const transform = (data: Record<string, any>) => {
        // Only send fields the form manages — exclude legacy flat fields
        // and read-only fields to avoid validation errors
        const allowedFields = [
            'guid',
            'key',
            'name',
            'is_default',
            'type',
            'info',
            'status',
        ];
        const cleaned: Record<string, any> = {};
        for (const key of allowedFields) {
            if (key in data && data[key] !== null && data[key] !== undefined) {
                cleaned[key] = data[key];
            }
        }
        return cleaned;
    };

    return (
        <Edit
            resource="admin/api-configs"
            redirect={false}
            mutationMode="pessimistic"
            actions={<EditActions />}
            queryOptions={{ refetchOnMount: 'always' }}
            transform={transform}
        >
            <SimpleForm toolbar={<EditToolbar />}>
                <ApiConfigEditForm />
            </SimpleForm>
        </Edit>
    );
};

export default ApiConfigEdit;
